import React, { useState, useCallback } from "react";
import { Wifi, WifiOff, Loader2, RotateCw } from "lucide-react";
import { useConnectionStatus, forceReconnect, FORCE_RECONNECT_LOCK_MS } from "@/lib/events";

/**
 * Header badge showing SSE (canlı push) connection state.
 * States: open (green dot) · connecting (spinner) · offline (red icon).
 * `idle` (no active subscribers) is not rendered.
 *
 * iter237 — Small retry icon appears next to the badge on click.  Uses the
 * existing health-check + reconnect plumbing (never a full page reload,
 * never clears form state).  Debounced by FORCE_RECONNECT_LOCK_MS.
 */
export default function LiveStatusBadge() {
  const status = useConnectionStatus();
  const [checking, setChecking] = useState(false);
  const [failMsg, setFailMsg] = useState(null);

  const onRetry = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (checking) return;
    setChecking(true);
    setFailMsg(null);
    try {
      const r = await forceReconnect();
      if (!r.ok && r.reason === "unreachable") {
        setFailMsg("Bağlantı kurulamadı");
        setTimeout(() => setFailMsg(null), 4000);
      }
    } finally {
      // Release local UI lock after the shared debounce window elapses so
      // the user sees the spinner for the same duration the module lock
      // is active.
      setTimeout(() => setChecking(false), FORCE_RECONNECT_LOCK_MS);
    }
  }, [checking]);

  if (status === "idle") return null;

  const meta = {
    open: {
      cls: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
      dot: "bg-emerald-500",
      icon: <Wifi size={12} />,
      label: "Canlı",
      title: "Canlı push bağlantısı aktif — envanter/PO değişiklikleri anlık yansır.",
      testid: "live-status-open",
    },
    connecting: {
      cls: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30",
      dot: "bg-amber-500 animate-pulse",
      icon: <Loader2 size={12} className="animate-spin" />,
      label: "Bağlanıyor",
      title: "Sunucuya canlı push bağlantısı kuruluyor…",
      testid: "live-status-connecting",
    },
    offline: {
      cls: "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30",
      dot: "bg-red-500 animate-pulse",
      icon: <WifiOff size={12} />,
      label: "Bağlantı kesildi",
      title: "Canlı push bağlantısı kesildi — otomatik olarak yeniden bağlanılıyor.",
      testid: "live-status-offline",
    },
  }[status] || null;
  if (!meta) return null;

  const busyLabel = checking ? "Kontrol ediliyor…" : (failMsg || meta.label);

  return (
    <div className="hidden md:inline-flex items-center gap-1">
      <div
        className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[11px] font-medium select-none ${meta.cls}`}
        title={failMsg || meta.title}
        data-testid={meta.testid}
        aria-live="polite"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
        {checking ? <Loader2 size={12} className="animate-spin" /> : meta.icon}
        <span>{busyLabel}</span>
      </div>
      <button
        type="button"
        onClick={onRetry}
        disabled={checking}
        title="Bağlantıyı yeniden dene"
        aria-label="Bağlantıyı yeniden dene"
        data-testid="live-status-retry"
        className="p-1 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <RotateCw size={12} className={checking ? "animate-spin" : ""} />
      </button>
    </div>
  );
}
